/**
 * @file     api.ts
 * @purpose  Browser calls for the owner-listing surfaces. Every write goes
 *           through secureFetch (CSRF) to the owner routes under
 *           /api/listings; responses keep the route's error code and details
 *           so screens can say what to fix.
 */
'use client';

import { secureFetch } from '@/services/anti-scraping/secureFetch';
import type { DraftPatch, ListingMediaRow, ListingSubmissionRow } from '@/services/listing-submissions/listingSubmissionModel';
import type { MlsDuplicateResult } from '@/services/listing-submissions/mlsDuplicate';
import type { RentComparable, RentRangeResult } from '@/services/listing-submissions/rentRange';
import type { QualityHint } from '@/services/listing-submissions/listingMedia';
import type { ListingEndReason } from '@/core/config/listingLifecycle';

export class ListingApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly details: string[] = [],
  ) {
    super(code);
  }
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await secureFetch(url, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => null)) as (T & { error?: string; details?: string[] }) | null;
  if (!res.ok) throw new ListingApiError(res.status, body?.error ?? 'server_error', body?.details ?? []);
  return body as T;
}

const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });
const draftUrl = (id: string) => `/api/listings/drafts/${encodeURIComponent(id)}`;

export const listingApi = {
  listDrafts: () => call<{ listings: ListingSubmissionRow[] }>('/api/listings/drafts'),
  createDraft: (kind: string) => call<{ listing: ListingSubmissionRow }>('/api/listings/drafts', json('POST', { kind, device: 'web' })),
  getDraft: (id: string) => call<{ listing: ListingSubmissionRow; media: ListingMediaRow[]; thumbs: Record<string, string> }>(draftUrl(id)),
  saveDraft: (id: string, patch: DraftPatch & { reviewed?: boolean }) =>
    call<{ listing: ListingSubmissionRow }>(draftUrl(id), json('PATCH', { ...patch, device: 'web' })),
  transition: (id: string, event: string, extra: { endReason?: ListingEndReason; leasedRent?: number; tenancyStartsOn?: string } = {}) =>
    call<{ listing: ListingSubmissionRow }>(`${draftUrl(id)}/transition`, json('POST', { event, ...extra })),
  mlsCheck: (id: string) => call<MlsDuplicateResult>(`${draftUrl(id)}/mls-check`),
  requestUpload: (id: string, file: { mime: string; bytes: number }) =>
    call<{ upload: { mediaId: string; path: string; token: string; signedUrl: string } }>(`${draftUrl(id)}/media`, json('POST', file)),
  finalizeUpload: (id: string, mediaId: string) =>
    call<{ media: ListingMediaRow; hints: QualityHint[]; completeness: number; thumbUrl: string | null }>(`${draftUrl(id)}/media/${encodeURIComponent(mediaId)}`, json('POST')),
  removeMedia: (id: string, mediaId: string) =>
    call<{ ok: true; completeness: number }>(`${draftUrl(id)}/media/${encodeURIComponent(mediaId)}`, json('DELETE')),
  reorderMedia: (id: string, order: string[]) => call<{ ok: true; completeness: number }>(`${draftUrl(id)}/media`, json('PATCH', { order })),
  verifyPhone: (id: string, body: { action: 'start' | 'check'; phone: string; code?: string }) =>
    call<{ ok: boolean; phoneVerifiedAt?: string }>(`${draftUrl(id)}/verify-phone`, json('POST', body)),
  attest: (id: string, body: { role: 'owner' | 'authorised'; legalName: string; confirmed: boolean }) =>
    call<{ listing: ListingSubmissionRow }>(`${draftUrl(id)}/attestation`, json('POST', body)),
  fairHousing: (text: string) => call<{ flags: FairHousingFlagDto[] }>('/api/listings/fair-housing/check', json('POST', { text })),
  leaseValue: (body: { address?: string; latitude?: number; longitude?: number; bedrooms: number; homeType: string }) =>
    call<LeaseValueResponse>('/api/listings/kinds/lease/value', json('POST', body)),
};

export type FairHousingFlagDto = {
  ruleId: string;
  ground: string;
  severity: 'block' | 'warn';
  start: number;
  end: number;
  match: string;
  reason: string;
  suggestion: string | null;
};

export type LeaseValueResponse =
  | { asking: RentRangeResult; leasedLocked: true; leased?: undefined; comparables?: undefined }
  | { asking: RentRangeResult; leased: RentRangeResult | null; comparables: RentComparable[] | null; leasedLocked?: undefined };
